import React, { useContext, useEffect, useState } from "react";
import { Form, useForm, useFormState } from "react-final-form";
import arrayMutators from "final-form-arrays";
import { Radio, RadixSelect, Textarea, TextField } from "components/Form/Field";
import { IntlContext } from "context";
import { AdFrequency } from "types/ad";
import { Category } from "types/category";
import { Options } from "ui/Form/Element/Select";
import { useCreateAd, useDeleteAd, useUpdateAd } from "hooks/ad";
import { AdInterface } from "types/ad";
import formatErrorResponse from "helpers/form/formatErrorResponse";
import { Button } from "components/ui/button";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "components/ui/dialog";
import { useNavigate } from "react-router";
import Address from "components/Form/Address";
import blobToBase64 from "helpers/file/blobToBase64";
import { getActiveServiceCategoryOptions } from "helpers/category/getActiveServiceCategoryOptions";

import { renderCategoryFields } from "../../common/CategoryFields/renderCategoryFields";
import { ROUTES } from "routes";
import AdPhotoField from "./AdPhotoField";
import {
    profileFieldControlProps,
    profileFieldGroupProps,
    profileInputClassName,
    profileLabelProps,
    profileTextareaClassName,
} from "./formStyles";

function normalizeAdData(data: unknown): Record<string, unknown> {
    const d: Record<string, unknown> = !data || Array.isArray(data) ? {} : (data as Record<string, unknown>);
    return {
        ...d,
        bring_products: d.bring_products !== undefined && d.bring_products !== null
            ? String(Number(d.bring_products))
            : "0",
        bring_tools: d.bring_tools !== undefined && d.bring_tools !== null
            ? String(Number(d.bring_tools))
            : "0",
    };
}

function buildAddressInitialValues(ad: AdInterface | undefined) {
    const lat = ad?.coordinates_lat;
    const lon = ad?.coordinates_lon;

    const addressText = ad?.street
        ? [ad.house_number, ad.street, ad.postcode, ad.city].filter(Boolean).join(" ")
        : "";

    return {
        address: addressText,
        coordinates_lat: lat !== undefined && lat !== null ? String(lat) : undefined,
        coordinates_lon: lon !== undefined && lon !== null ? String(lon) : undefined,
        house_number: ad?.house_number,
        street: ad?.street,
        postcode: ad?.postcode,
        city: ad?.city,
    };
}

export enum Mode {
    ADD = "ADD",
    VIEW = "VIEW",
    EDIT = "EDIT",
}

const AD_DESCRIPTION_MIN_LENGTH = 30;
const AD_DESCRIPTION_MAX_LENGTH = 2000;

interface AdFormProps {
    mode?: Mode;
}

const AdForm: React.FC<AdFormProps> = ({ mode }) => {
    const intl = useContext(IntlContext);
    const navigate = useNavigate();
    const deleteAdMutation = useDeleteAd();

    const form = useForm();
    const { values, submitting } = useFormState();
    const [pastCategory, setPastCategory] = useState<string>(values.category);
    const categoriesWithoutSub = [Category.CHILDCARE.toString()];

    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const handleOnClickDeleteAd = async () => {
        await deleteAdMutation.mutateAsync(values.id);

        navigate(ROUTES.MY_ADS);
        toast.success(intl.formatMessage("ad.message.delete_success"));
    };

    useEffect(() => {
        if (!values.category) {
            form.change("subcategory", undefined);
        } else if (values.category !== pastCategory) {
            form.change("subcategory", undefined);
            setPastCategory(values.category);
        }
    }, [values.category, form, pastCategory]);

    const isReadOnly = mode === Mode.VIEW;

    const descriptionLength = String(values?.description ?? "").length;
    const hasMinDescriptionLength = descriptionLength >= AD_DESCRIPTION_MIN_LENGTH;

    const categories = getActiveServiceCategoryOptions(intl);

    return (
        <div className="space-y-8">
            <TextField
                inputProps={{
                    name: "title",
                    placeholder: intl.formatMessage("ad.label.title.placeholder"),
                    disabled: isReadOnly,
                    required: true,
                    className: profileInputClassName,
                }}
                labelProps={profileLabelProps(intl.formatMessage("ad.label.title"), true)}
                fieldGroupProps={profileFieldGroupProps()}
            />

            {mode === Mode.ADD && (
                <div className="grid gap-6 sm:grid-cols-2">
                    <RadixSelect
                        inputProps={{
                            name: "category",
                            placeholder: intl.formatMessage("service.placeholder.categories"),
                            required: true,
                            className: profileInputClassName,
                            options: categories,
                        }}
                        labelProps={profileLabelProps(intl.formatMessage("service.label.categories"), true)}
                        fieldGroupProps={profileFieldGroupProps()}
                    />

                    {values?.category && categoriesWithoutSub.indexOf(values.category) === -1 && (
                        <RadixSelect
                            inputProps={{
                                name: "subcategory",
                                placeholder: intl.formatMessage("service.placeholder.subcategories"),
                                required: true,
                                disabled: !values.category,
                                className: profileInputClassName,
                                options: values?.category
                                    ? Object.entries(
                                          Category[values.category as keyof typeof Category].values
                                      ).map(([_key, value]) => {
                                          const stringValue = value.toString();
                                          return {
                                              label: intl.formatMessage(
                                                  `service.label.subcategory.${stringValue}`
                                              ),
                                              value: stringValue,
                                          } as Options;
                                      })
                                    : [],
                            }}
                            labelProps={profileLabelProps(intl.formatMessage("service.label.subcategories"), true)}
                            fieldGroupProps={profileFieldGroupProps()}
                        />
                    )}
                </div>
            )}

            <div className="space-y-6">
                {renderCategoryFields(values?.subcategory, {
                    disabled: isReadOnly,
                    isAd: true,
                })}

                <div className={profileFieldGroupProps().className}>
                    <Textarea
                        inputProps={{
                            name: "description",
                            placeholder: intl.formatMessage("ad.label.description.placeholder"),
                            disabled: isReadOnly,
                            required: true,
                            minLength: AD_DESCRIPTION_MIN_LENGTH,
                            maxLength: AD_DESCRIPTION_MAX_LENGTH,
                            rows: 6,
                            className: profileTextareaClassName,
                        }}
                        labelProps={profileLabelProps(intl.formatMessage("service.label.description"), true)}
                        fieldGroupProps={profileFieldGroupProps("space-y-0")}
                    />
                    <div className="flex w-full items-center justify-end gap-4 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                        <div className="flex items-center gap-4">
                            <div>
                                <span className="text-primary">{descriptionLength}</span>/{AD_DESCRIPTION_MAX_LENGTH}{" "}
                                caractères
                            </div>
                            <div>
                                Min.{" "}
                                <span
                                    className={
                                        hasMinDescriptionLength ? "text-emerald-600" : "text-red-600"
                                    }
                                >
                                    {AD_DESCRIPTION_MIN_LENGTH}
                                </span>{" "}
                                caractères
                            </div>
                        </div>
                    </div>
                </div>

                <div className={profileFieldGroupProps().className}>
                    <p className="text-sm text-slate-500">
                        {intl.formatMessage("profile.my_ads.form.address.description")}
                    </p>
                    <Address
                        inputProps={{
                            name: "address",
                            latitudeName: "coordinates_lat",
                            longitudeName: "coordinates_lon",
                            houseNumber: "house_number",
                            street: "street",
                            postcode: "postcode",
                            city: "city",
                            required: true,
                            placeholder: "ex. 12 rue de la Paix 44000 Nantes",
                        }}
                        labelProps={profileLabelProps(intl.formatMessage("ad.label.address"), true)}
                        fieldControlProps={profileFieldControlProps(
                            "relative [&_.field-control__autocomplete]:block [&_input]:h-11 [&_input]:rounded-xl [&_input]:border-gray-200 [&_input]:bg-white"
                        )}
                    />
                </div>

                <AdPhotoField disabled={isReadOnly} existingPictureId={values.picture_id} />
            </div>

            {mode === Mode.EDIT && (
                <Radio
                    fieldControlProps={{ className: "field-control-radio field__row--inline" }}
                    inputProps={{
                        name: "is_active",
                        required: true,
                        disabled: false,
                        options: [
                            { label: intl.formatMessage("ad.card.status_active"), value: "true" },
                            { label: intl.formatMessage("ad.card.status_closed"), value: "false" },
                        ],
                    }}
                    labelProps={profileLabelProps(intl.formatMessage("ad.card.form_label_status"), true)}
                />
            )}

            {mode !== Mode.VIEW && (
                <div className="mt-8 flex flex-col-reverse gap-3 border-t border-gray-100 pt-6 sm:flex-row sm:items-center sm:justify-between">
                    {mode === Mode.EDIT && (
                        <Button
                            type="button"
                            variant="outline"
                            className="rounded-xl text-destructive border-destructive hover:bg-destructive/10 hover:text-destructive"
                            onClick={() => setShowDeleteModal(true)}
                        >
                            {intl.formatMessage("ad.label.delete")}
                        </Button>
                    )}
                    <Button
                        type="submit"
                        className="rounded-xl sm:ml-auto sm:min-w-[220px]"
                        disabled={submitting || !hasMinDescriptionLength}
                    >
                        {mode === Mode.EDIT
                            ? intl.formatMessage("ad.label.validate")
                            : intl.formatMessage("ad.label.publish")}
                    </Button>

                    <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
                        <DialogContent className="sm:max-w-md">
                            <DialogHeader>
                                <DialogTitle>{intl.formatMessage("ad.label.modal.title")}</DialogTitle>
                                <DialogDescription>
                                    {intl.formatMessage("ad.label.modal.message")}
                                </DialogDescription>
                            </DialogHeader>
                            <DialogFooter className="gap-2 sm:gap-0">
                                <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
                                    {intl.formatMessage("generic.action.cancel")}
                                </Button>
                                <Button
                                    variant="destructive"
                                    disabled={deleteAdMutation.isPending}
                                    onClick={() => void handleOnClickDeleteAd()}
                                >
                                    {intl.formatMessage("generic.action.confirm")}
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </div>
            )}
        </div>
    );
};

interface AdFormWrapperProps {
    initialValues?: AdInterface;
    mode?: Mode;
}

const EMPTY_AD_INITIAL_VALUES = {} as AdInterface;

const AdFormWrapper: React.FC<AdFormWrapperProps> = ({
    initialValues = EMPTY_AD_INITIAL_VALUES,
    mode = Mode.EDIT,
}) => {
    const intl = useContext(IntlContext);
    const navigate = useNavigate();
    const createAdMutation = useCreateAd();
    const updateAdMutation = useUpdateAd();

    const formInitialValues = React.useMemo(
        () => ({
            ...(mode === Mode.EDIT ? buildAddressInitialValues(initialValues as AdInterface | undefined) : {}),
            ...initialValues,
            data: normalizeAdData((initialValues as AdInterface | undefined)?.data),
            is_active: (initialValues as AdInterface | undefined)?.is_active !== undefined
                ? String((initialValues as AdInterface).is_active)
                : "true",
            frequency: (initialValues as AdInterface | undefined)?.frequency ?? AdFrequency.ONE_TIME,
        }),
        [initialValues, mode]
    );

    const isRecord = (value: unknown): value is Record<string, unknown> =>
        typeof value === "object" && value !== null;

    type AdFormValues = Record<string, unknown> & {
        photo?: { content?: unknown };
    };

    const onSubmit = async (values: AdFormValues) => {
        if (values?.photo?.content && values.photo.content instanceof Blob) {
            values.photo.content = await blobToBase64(values.photo.content);
        }

        // Ad API expects address fields (coordinates, street, etc.).
        // Keep them in the payload; profile update is handled elsewhere.
        const payload = { ...values };
        delete payload.duration;
        if (payload.is_active !== undefined) {
            payload.is_active = payload.is_active === "true" || payload.is_active === true;
        }

        try {
            if (initialValues?.id) {
                await updateAdMutation.mutateAsync({
                    id: initialValues.id,
                    data: payload as unknown as HashMap<unknown>,
                });
                toast.success(intl.formatMessage("ad.message.update_success"));
            } else {
                await createAdMutation.mutateAsync(payload as unknown as HashMap<unknown>);
                toast.success(intl.formatMessage("ad.message.create_success"));
            }
            navigate(ROUTES.MY_ADS);
        } catch (err: unknown) {
            if (isRecord(err) && "response" in err && isRecord(err.response) && "data" in err.response) {
                const responseData = err.response.data;
                if (isRecord(responseData) && "data" in responseData) {
                    return formatErrorResponse(responseData as { data: unknown });
                }
            }
            toast.error(intl.formatMessage("ad.message.update_error"));
            throw err;
        }
    };

    return (
        <Form
            onSubmit={onSubmit}
            initialValues={formInitialValues}
            mutators={{ ...arrayMutators }}
            render={({ handleSubmit }) => (
                <form onSubmit={handleSubmit}>
                    <AdForm mode={mode} />
                </form>
            )}
        />
    );
};

export default AdFormWrapper;
